import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ENVIRONMENTS, ENVIRONMENT_BY_ID } from '@/deploy/types';
import type { EnvironmentId, MinecraftContainer, ProjectGroup } from '@/deploy/types';
import { useContainersStore } from '@/deploy/stores/containers.store';
import { environmentsService, type EnvironmentInstance } from '@/deploy/services/environments.service';
import { devEnvService, type DevEnv } from '@/deploy/services/dev-env.service';
import { betaSlotService, type BetaSlot } from '@/deploy/services/beta-slot.service';
import { ServerControls } from '@/deploy/components/ui/ServerControls';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { Avatar } from '@/deploy/components/ui/Avatar';
import { PermissionLock } from '@/deploy/components/ui/PermissionLock';
import { Modal } from '@/deploy/components/ui/Modal';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useDeployEnvStore, isDeployEnv } from '@/deploy/stores/deployEnv.store';
import { useProjectGameGroups } from '@/deploy/hooks/useProjectGameGroups';
import { MOCK_APIS, MOCK_INSTANCES } from '@/deploy/data/mock';
import { can } from '@/deploy/utils/permissions';
import { formatNumber, formatRelative } from '@/deploy/utils/format';
import {
  GROUP_FILTER_ALL, GROUP_FILTER_UNKNOWN, groupDisplay, groupFilterChips, instanceGroupState, matchesGroupFilter,
  unknownGroups, unknownGroupsMessage, validGroupFilter,
} from '@/deploy/utils/instanceGroups';
import type { UpdateProcedure } from '@/deploy/services/procedure.service';
import type { PromotionPlan } from '@/deploy/services/promotion.service';
import styles from './Environments.module.scss';

export function Environments() {
  const params = useParams<{ envId?: EnvironmentId }>();
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());

  const envId: EnvironmentId = (params.envId as EnvironmentId) ?? 'prod';

  // Mémorise l'env courant dans le store partagé (repris par APIs / Base de données / Resource pack).
  const setSharedEnv = useDeployEnvStore((s) => s.setEnv);
  useEffect(() => { if (isDeployEnv(envId)) setSharedEnv(envId); }, [envId, setSharedEnv]);

  const envTabs = (
    <nav className={styles.envTabs}>
      {ENVIRONMENTS.map((e) => (
        <button
          key={e.id}
          className={[styles.tab, e.id === envId && styles.tabActive].filter(Boolean).join(' ')}
          onClick={() => navigate(`/deploiement/environments/${e.id}`)}
        >
          <EnvBadge env={e.id} size="sm" />
          <span>{e.name}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className={styles.page}>
      {/* Prod/préprod affichent les onglets sur la même ligne que les actions (cf. StableView). */}
      {(envId === 'dev' || envId === 'beta') && envTabs}

      {envId === 'dev'     && <DevView projectId={project.id} />}
      {envId === 'beta'    && <BetaView projectId={project.id} />}
      {envId === 'preprod' && <StableView tabs={envTabs} envId="preprod" projectId={project.id} />}
      {envId === 'prod'    && <StableView tabs={envTabs} envId="prod"    projectId={project.id} />}
    </div>
  );
}

// ============================================================
//  DEV — 1 serveur Ptero par dev, base = un container Recube.
//  Le run.sh injecté symlink /home/container → /mcserv pour
//  qu'on puisse tout modifier depuis le file explorer Ptero.
// ============================================================

function DevView({ projectId }: { projectId: string }) {
  const user = useSessionStore((s) => s.user);
  // Groupes de jeu (NationsGlory seulement) : choix à la création, badge sur la carte
  const project = useSessionStore((s) => s.currentProject());
  const { enabled: groupsEnabled, groups: gameGroups } = useProjectGameGroups(project);
  const [envs, setEnvs] = useState<DevEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const canManage = can(user, 'env.dev.manage');

  const reload = async () => {
    setError(null);
    try {
      const list = await devEnvService.list(projectId);
      setEnvs(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Polling doux tant qu'au moins un env est en cours de provisioning
  useEffect(() => {
    const anyPending = envs.some((e) => e.status === 'creating' || e.status === 'installing');
    if (!anyPending) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envs]);

  const myEnvs = envs.filter((e) => e.ownerId === user.id);
  const otherEnvs = envs.filter((e) => e.ownerId !== user.id);

  return (
    <>
      <PageHeader
        eyebrow="Dev"
        title={<>Environnements de <span className={styles.projectName}>développement</span></>}
        subtitle={`${envs.length} env${envs.length > 1 ? 's' : ''} · un serveur Ptero par dev, cloné depuis un container Recube`}
        actions={
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setCreateOpen(true)}
            disabled={!canManage}
          >
            Créer mon env de dev
            {!canManage && <PermissionLock inline />}
          </Button>
        }
      />

      {error && (
        <div style={{
          padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
          borderRadius: 8, fontSize: 13, marginBottom: 12,
        }}>
          <Icon name="warn" size={12} /> {error}
        </div>
      )}

      {loading ? (
        <div className={styles.empty}>Chargement des envs de dev…</div>
      ) : (
        <>
          <section>
            <h2 className={styles.groupLabel}>Mes envs ({myEnvs.length})</h2>
            {myEnvs.length === 0 ? (
              <div className={styles.empty}>Tu n'as pas encore d'env de dev.</div>
            ) : (
              <div className={styles.devGrid}>
                {myEnvs.map((e) => <DevEnvCard key={e.id} projectId={projectId} env={e} mine onChange={reload} groups={gameGroups} />)}
              </div>
            )}
          </section>

          <section>
            <h2 className={styles.groupLabel}>Autres devs ({otherEnvs.length})</h2>
            {otherEnvs.length === 0 ? (
              <div className={styles.empty}>Aucun autre env actif.</div>
            ) : (
              <div className={styles.devGrid}>
                {otherEnvs.map((e) => <DevEnvCard key={e.id} projectId={projectId} env={e} onChange={reload} groups={gameGroups} />)}
              </div>
            )}
          </section>
        </>
      )}

      {createOpen && (
        <CreateDevEnvModal
          projectId={projectId}
          groups={groupsEnabled ? gameGroups : null}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); void reload(); }}
        />
      )}
    </>
  );
}

const STATUS_LABEL: Record<DevEnv['status'], { label: string; tone: 'muted' | 'info' | 'warning' | 'success' | 'danger' }> = {
  creating: { label: 'Création…', tone: 'info' },
  installing: { label: 'Install Ptero…', tone: 'info' },
  running: { label: 'En marche', tone: 'success' },
  stopped: { label: 'Arrêté', tone: 'muted' },
  errored: { label: 'Erreur', tone: 'danger' },
};

function DevEnvCard({
  projectId, env, mine, onChange, groups = null,
}: {
  projectId: string; env: DevEnv; mine?: boolean; onChange: () => void;
  /** Groupes de jeu du projet (null : non chargés ou projet sans groupes). */
  groups?: ProjectGroup[] | null;
}) {
  // Groupe choisi à la création : seuls les envs NationsGlory créés avec un groupe en ont un
  const group = groupDisplay(env.group, groups);
  const user = useSessionStore((s) => s.user);
  const navigate = useNavigate();
  const [pushOpen, setPushOpen] = useState(false);
  const [busy, setBusy] = useState<'delete' | 'push' | null>(null);
  const canManage = mine || user.role === 'admin';
  const actingAsAdmin = !mine && user.role === 'admin';
  const status = STATUS_LABEL[env.status] ?? { label: env.status, tone: 'muted' as const };
  const pending = env.status === 'creating' || env.status === 'installing';

  const remove = async () => {
    if (!confirm(`Supprimer définitivement l'env "${env.displayName}" ? (Ptero + Mongo)`)) return;
    setBusy('delete');
    try {
      await devEnvService.remove(projectId, env.id);
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression');
      setBusy(null);
    }
  };

  // Le promote (stop + wipe + cp NFS + reinstall) dure plusieurs minutes : la popup se ferme
  // aussitôt et l'opération tourne en arrière-plan (le bouton reste en "chargement").
  const runPushToBeta = (slot: string) => {
    setBusy('push');
    betaSlotService.promote(projectId, slot, env.id)
      .then(() => onChange())
      .catch((e) => alert(`Pousser vers Bêta ${slot} a échoué : ${e instanceof Error ? e.message : String(e)}`))
      .finally(() => setBusy(null));
  };

  return (
    <>
      <article className={[styles.devCard, mine && styles.devMine].filter(Boolean).join(' ')}>
        <header
          className={styles.devCardHead}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          title="Ouvrir le détail (console + fichiers)"
          onClick={() => navigate(`/deploiement/environments/dev/${env.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/deploiement/environments/dev/${env.id}`); }
          }}
        >
          <Avatar name={env.ownerName} color={env.ownerAvatarColor ?? '#888'} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={styles.devName}>{env.displayName}</div>
            <div className={styles.devOwner}>
              {env.ownerName}
              {env.createdAt && <> · créé {formatRelative(env.createdAt)}</>}
            </div>
          </div>
          <Badge tone={status.tone} dot>{status.label}</Badge>
          <Icon name="chevron-right" size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
        </header>

        <div className={styles.devBody}>
          <div className={styles.devRow}>
            <span className={styles.devRowLabel}>Base</span>
            <Badge tone="info">
              <Icon name="package" size={12} /> {env.containerCode}
            </Badge>
          </div>
          {group && (
            <div className={styles.devRow}>
              <span className={styles.devRowLabel}>Groupe</span>
              <GroupBadge group={group} size="md" title={`RECUBE_GROUP = ${env.group} à la création`} />
            </div>
          )}
          {env.dockerImage && (
            <div className={styles.devRow}>
              <span className={styles.devRowLabel}>Image</span>
              <code className={styles.mono} style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{env.dockerImage}</code>
              {canManage && (
                <button type="button"
                  onClick={async () => {
                    const cur = env.dockerImage ?? '';
                    const next = window.prompt('Nouvelle image Docker (tag inclus, ex harbor.../faction:1.2.3) :', cur);
                    if (!next || next === cur) return;
                    try {
                      await devEnvService.updateImage(projectId, env.id, next.trim());
                      onChange();
                    } catch (e) {
                      alert('Update KO : ' + (e instanceof Error ? e.message : 'Erreur'));
                    }
                  }}
                  style={{ padding: '2px 8px', fontSize: 10,
                    border: '1px solid var(--color-border)', background: 'transparent',
                    color: 'var(--color-text-muted)', borderRadius: 4, cursor: 'pointer' }}
                  title="Modifier l'image (snapshot figé — pas d'auto-update)">
                  Modifier
                </button>
              )}
            </div>
          )}
          {env.pteroIdentifier && (
            <div className={styles.devRow}>
              <span className={styles.devRowLabel}>Ptero</span>
              <code className={styles.mono} style={{ fontSize: 11 }}>{env.pteroIdentifier}</code>
            </div>
          )}
          {env.publicAddress && (
            <div className={styles.devRow}>
              <span className={styles.devRowLabel}>IP</span>
              <code className={styles.mono} style={{ fontSize: 11, flex: 1 }}>{env.publicAddress}</code>
              <button type="button"
                onClick={() => { void navigator.clipboard.writeText(env.publicAddress!); }}
                style={{ padding: '2px 8px', fontSize: 10,
                  border: '1px solid var(--color-border)', background: 'transparent',
                  color: 'var(--color-text-muted)', borderRadius: 4, cursor: 'pointer' }}>
                Copier
              </button>
            </div>
          )}
          {env.errorMessage && (
            <div className={styles.devRow}>
              <span className={styles.devRowLabel}>Erreur</span>
              <span style={{ color: 'var(--color-danger)', fontSize: 11 }}>{env.errorMessage}</span>
            </div>
          )}
        </div>

        <footer className={styles.devCardFoot}>
          {canManage && env.pteroIdentifier && !pending && (
            <ServerControls
              projectId={projectId}
              scope="dev"
              targetKey={env.id}
              sourceName={env.displayName}
              sourceSubtitle={`dev · ${env.containerCode}`}
              icon="server"
              onAction={() => onChange()}
            />
          )}
          {canManage && env.pteroServerId && (
            <Button variant="secondary" size="sm" leftIcon={<Icon name="arrow-right" size={12} />}
              disabled={pending || busy !== null} loading={busy === 'push'}
              onClick={() => setPushOpen(true)}>
              Pousser en Bêta
              {actingAsAdmin && <Badge tone="warning" className={styles.adminBadge}>admin</Badge>}
            </Button>
          )}
          {canManage && (
            <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={12} />}
              disabled={busy !== null} loading={busy === 'delete'}
              onClick={() => void remove()}>
              Supprimer
            </Button>
          )}
        </footer>
      </article>
      {pushOpen && (
        <PushToBetaModal projectId={projectId} env={env}
          onClose={() => setPushOpen(false)}
          onLaunch={(slot) => { setPushOpen(false); runPushToBeta(slot); }}
        />
      )}
    </>
  );
}

function CreateDevEnvModal({
  projectId, groups, onClose, onCreated,
}: {
  projectId: string;
  /** Groupes de jeu proposés (NationsGlory) ; null ou vide : pas de choix, défaut de l'egg. */
  groups: ProjectGroup[] | null;
  onClose: () => void; onCreated: () => void;
}) {
  const containersAll = useContainersStore((s) => s.containers);
  const loadContainers = useContainersStore((s) => s.load);
  useEffect(() => { void loadContainers(projectId); }, [projectId, loadContainers]);
  const containers = useMemo(
    () => containersAll.filter((c) => c.projectId === projectId),
    [containersAll, projectId],
  );

  const [displayName, setDisplayName] = useState('');
  const [containerCode, setContainerCode] = useState<string>('');
  // '' = défaut de l'egg (aucun groupe imposé), comme avant les groupes
  const [group, setGroup] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const groupChoices = groups ?? [];

  useEffect(() => {
    if (!containerCode && containers.length > 0) setContainerCode(containers[0].code);
  }, [containers, containerCode]);

  // Groupe retiré entre-temps (liste relue au CICD) : retour au défaut de l'egg
  useEffect(() => {
    if (group && !groupChoices.some((g) => g.key === group)) setGroup('');
  }, [group, groupChoices]);

  const submit = async () => {
    if (!containerCode) { setError('Choisis un container de base'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await devEnvService.create(projectId, {
        displayName: displayName.trim() || undefined,
        containerCode,
        // Absent sans choix : le corps envoyé est celui d'avant les groupes
        ...(group ? { group } : {}),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={submitting ? () => {} : onClose}
      title="Créer un env de dev"
      subtitle="Un serveur Ptero est créé, run.sh injecté puis PATCH startup. Fichiers modifiables via /files."
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={() => void submit()} disabled={submitting || !containerCode}
          loading={submitting} leftIcon={<Icon name="sparkles" size={14} />}>
          Lancer la création
        </Button>
      </>}
    >
      <div className={styles.createForm}>
        {error && (
          <div style={{
            padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
            borderRadius: 8, fontSize: 13,
          }}>
            <Icon name="warn" size={12} /> {error}
          </div>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Nom (optionnel)</span>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Env de test event Pâques…" disabled={submitting} />
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Container Recube à cloner</span>
          {containers.length === 0 ? (
            <div className={styles.empty}>Aucun container configuré pour ce projet.</div>
          ) : (
            <div className={styles.modelPicker}>
              {containers.map((c) => (
                <button key={c.code} type="button"
                  onClick={() => setContainerCode(c.code)}
                  disabled={submitting}
                  className={[styles.modelTile, containerCode === c.code && styles.modelActive].filter(Boolean).join(' ')}
                  style={{ ['--model-color' as string]: c.color ?? '#888' }}
                >
                  <span className={styles.modelChip} style={{ background: c.color ?? '#888' }}>
                    <Icon name="package" size={14} />
                  </span>
                  <span className={styles.modelName}>{c.displayName ?? c.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {groupChoices.length > 0 && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Groupe de jeu</span>
            <select value={group} onChange={(e) => setGroup(e.target.value)} disabled={submitting}>
              <option value="">Défaut de l'egg (aucun groupe imposé)</option>
              {groupChoices.map((g) => (
                <option key={g.key} value={g.key}>{g.label || g.key}</option>
              ))}
            </select>
            <span className={styles.fieldHint}>
              Posé dans la variable RECUBE_GROUP du serveur : il reçoit les API de ce groupe.
              L'egg des dev envs doit déclarer cette variable, sinon la création est refusée.
            </span>
          </label>
        )}

        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
          <Icon name="sparkles" size={11} /> La création prend ~30s à 2 min (install Ptero + injection run.sh).
        </p>
      </div>
    </Modal>
  );
}

export function PushToBetaModal({
  projectId, env, onClose, onLaunch,
}: {
  projectId: string; env: DevEnv; onClose: () => void; onLaunch: (slot: string) => void;
}) {
  const [slots, setSlots] = useState<BetaSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    betaSlotService.list(projectId)
      .then((list) => { setSlots(list); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [projectId]);

  // On ne LANCE pas le promote ici (plusieurs minutes) : on délègue au parent qui le fait
  // en arrière-plan, et on ferme la popup tout de suite (plus de blocage sur un spinner).
  const launch = () => {
    if (!target) return;
    onLaunch(target);
    onClose();
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Pousser "${env.displayName}" en Bêta`}
      subtitle="Le serveur beta cible est arrêté, ses fichiers wipés, puis clonés via NFS depuis le dev. Lancé en arrière-plan (quelques minutes)."
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" onClick={launch}
          disabled={!target}
          leftIcon={<Icon name="arrow-right" size={14} />}>
          Pousser vers Bêta {target ?? ''}
        </Button>
      </>}
    >
      {error && (
        <div style={{
          padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
          borderRadius: 8, fontSize: 13, marginBottom: 10,
        }}>
          <Icon name="warn" size={12} /> {error}
        </div>
      )}
      {loading ? (
        <div className={styles.empty}>Chargement des slots…</div>
      ) : (
        <div className={styles.slotsGrid}>
          {slots.map((s) => (
            <button key={s.slot} type="button"
              onClick={() => s.configured && setTarget(s.slot)}
              disabled={!s.configured}
              className={[
                styles.slotTile,
                target === s.slot && styles.slotActive,
                s.currentDevEnvId && styles.slotOccupied,
              ].filter(Boolean).join(' ')}
              title={!s.configured ? 'Slot non configuré (voir Settings > Ptero API)' : undefined}
            >
              <div className={styles.slotLetter}>{s.slot}</div>
              <div className={styles.slotStatus}>
                {!s.configured && <Badge tone="muted">Non configuré</Badge>}
                {s.configured && s.currentDevEnvId && (
                  <>
                    <Badge tone="warning">Occupé</Badge>
                    <span>{s.currentDevEnvDisplay}</span>
                  </>
                )}
                {s.configured && !s.currentDevEnvId && <Badge tone="success" dot>Libre</Badge>}
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ============================================================
//  BETA — 5 slots fixes A..E, servers Ptero pré-créés qui ne bougent jamais.
//  Promote = wipe + cp -rp via NFS depuis un dev env.
// ============================================================

function BetaView({ projectId }: { projectId: string }) {
  const user = useSessionStore((s) => s.user);
  const navigate = useNavigate();
  const canPromote = can(user, 'env.beta.promote');
  const [slots, setSlots] = useState<BetaSlot[]>([]);
  const [devEnvs, setDevEnvs] = useState<DevEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<BetaSlot | null>(null);
  const [busySlots, setBusySlots] = useState<Record<string, 'promote' | 'clear'>>({});

  const reload = async () => {
    setError(null);
    try {
      const [s, d] = await Promise.all([
        betaSlotService.list(projectId),
        devEnvService.list(projectId),
      ]);
      setSlots(s);
      setDevEnvs(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Promote/clear tournent plusieurs minutes côté backend : on les lance en arrière-plan,
  // le slot affiche « … en cours » et se recharge à la fin (plus de popup figée).
  const runSlotOp = (slot: string, action: 'promote' | 'clear', fn: () => Promise<unknown>) => {
    setError(null);
    setBusySlots((m) => ({ ...m, [slot]: action }));
    fn()
      .then(() => reload())
      .catch((e) => setError(e instanceof Error ? e.message : (action === 'clear' ? 'Vidage a échoué' : 'Promote a échoué')))
      .finally(() => setBusySlots((m) => { const n = { ...m }; delete n[slot]; return n; }));
  };

  const configuredCount = slots.filter((s) => s.configured).length;
  const occupiedCount = slots.filter((s) => s.currentDevEnvId).length;

  return (
    <>
      <PageHeader
        eyebrow="Bêta"
        title={<>5 slots de test · <span className={styles.projectName}>A → E</span></>}
        subtitle={
          loading
            ? 'Chargement…'
            : `${occupiedCount}/${configuredCount || 5} slots occupés · les serveurs Ptero derrière ne bougent jamais (IP fixe)`
        }
      />

      {error && (
        <div style={{
          padding: '10px 12px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
          borderRadius: 8, fontSize: 13, marginBottom: 12,
        }}>
          <Icon name="warn" size={12} /> {error}
        </div>
      )}

      {loading ? (
        <div className={styles.empty}>Chargement des slots…</div>
      ) : (
        <div className={styles.betaGrid}>
          {slots.map((s) => (
            <article key={s.slot}
              className={[
                styles.slotCard,
                s.currentDevEnvId && styles.slotCardOcc,
              ].filter(Boolean).join(' ')}
            >
              <header
                className={styles.slotHead}
                role={s.configured ? 'button' : undefined}
                tabIndex={s.configured ? 0 : undefined}
                style={s.configured ? { cursor: 'pointer' } : undefined}
                title={s.configured ? 'Ouvrir le détail (console + fichiers)' : undefined}
                onClick={s.configured ? () => navigate(`/deploiement/environments/beta/${s.slot}`) : undefined}
                onKeyDown={s.configured ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/deploiement/environments/beta/${s.slot}`); }
                } : undefined}
              >
                <span className={styles.slotBig}>{s.slot}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {!s.configured && <Badge tone="muted">Non configuré</Badge>}
                  {s.configured && s.currentDevEnvId && <Badge tone="warning" dot>Occupé</Badge>}
                  {s.configured && !s.currentDevEnvId && <Badge tone="success" dot>Libre</Badge>}
                  {s.configured && <Icon name="chevron-right" size={16} style={{ color: 'var(--color-text-muted)' }} />}
                </span>
              </header>

              {s.publicAddress && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', margin: '4px 0',
                  background: 'var(--color-background-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 999,
                  fontSize: 12, color: 'var(--color-text-secondary)',
                }} title="Adresse publique (derrière reverse proxy). Clic pour copier.">
                  <Icon name="server" size={11} />
                  <code style={{ fontFamily: 'var(--font-family-mono)', flex: 1 }}>{s.publicAddress}</code>
                  <button type="button"
                    onClick={() => { void navigator.clipboard.writeText(s.publicAddress!); }}
                    style={{ padding: '2px 6px', fontSize: 10,
                      border: '1px solid var(--color-border)', background: 'transparent',
                      borderRadius: 3, cursor: 'pointer', color: 'var(--color-text-muted)' }}
                    title="Copier">
                    Copier
                  </button>
                </div>
              )}

              {busySlots[s.slot] && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', margin: '4px 0', borderRadius: 6,
                  background: 'var(--color-primary-light)', color: 'var(--color-text)',
                  fontSize: 12,
                }}>
                  <Icon name="refresh" size={12} />
                  {busySlots[s.slot] === 'clear' ? 'Vidage en cours…' : 'Promotion en cours…'} (quelques minutes)
                </div>
              )}

              {!s.configured ? (
                <div className={styles.slotEmpty}>
                  <span>Aucun UUID Ptero configuré pour ce slot.</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    Renseigne-le dans Settings → Config Ptero APIs (dev).
                  </span>
                </div>
              ) : s.currentDevEnvId ? (
                <>
                  <div className={styles.slotLabel}>{s.currentDevEnvDisplay ?? 'Dev env inconnu'}</div>
                  <div className={styles.slotFooter}>
                    <span>
                      Poussé {s.lastPromotedAt && formatRelative(s.lastPromotedAt)}
                      {s.lastPromotedBy && <> par <strong>{s.lastPromotedBy}</strong></>}
                    </span>
                  </div>
                  <div className={styles.slotActions}>
                    <ServerControls
                      projectId={projectId}
                      scope="beta"
                      targetKey={s.slot}
                      sourceName={`Bêta ${s.slot}`}
                      sourceSubtitle={s.currentDevEnvDisplay}
                      icon="server"
                    />
                    <Button variant="ghost" size="sm"
                      leftIcon={<Icon name="arrow-right" size={12} />}
                      disabled={!canPromote || !!busySlots[s.slot]}
                      onClick={() => setPromoteTarget(s)}>
                      Remplacer
                      {!canPromote && <PermissionLock inline />}
                    </Button>
                    <Button variant="ghost" size="sm"
                      leftIcon={<Icon name="x" size={12} />}
                      disabled={!canPromote || !!busySlots[s.slot]}
                      onClick={() => {
                        if (confirm(`Vider le slot Bêta ${s.slot} ?\nLe serveur beta sera arrêté et ses fichiers effacés, le slot repassera « libre ».`)) {
                          runSlotOp(s.slot, 'clear', () => betaSlotService.clear(projectId, s.slot));
                        }
                      }}>
                      Vider le slot
                      {!canPromote && <PermissionLock inline />}
                    </Button>
                  </div>
                </>
              ) : (
                <div className={styles.slotEmpty}>
                  <span>Slot libre — prêt à accueillir un dev env.</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <ServerControls
                      projectId={projectId}
                      scope="beta"
                      targetKey={s.slot}
                      sourceName={`Bêta ${s.slot}`}
                      sourceSubtitle="slot libre"
                      icon="server"
                      compact
                    />
                    <Button variant="secondary" size="sm"
                      leftIcon={<Icon name="arrow-right" size={12} />}
                      disabled={!canPromote || !!busySlots[s.slot]}
                      onClick={() => setPromoteTarget(s)}>
                      Pousser un dev env
                    </Button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {promoteTarget && (
        <PromoteFromDevModal slot={promoteTarget}
          devEnvs={devEnvs.filter((e) => e.status === 'running' || e.status === 'stopped')}
          onClose={() => setPromoteTarget(null)}
          onLaunch={(devEnvId) => {
            const slot = promoteTarget.slot;
            setPromoteTarget(null);
            runSlotOp(slot, 'promote', () => betaSlotService.promote(projectId, slot, devEnvId));
          }}
        />
      )}

      <aside className={styles.infoBar}>
        <Icon name="warn" size={14} />
        <span>
          <strong>Rappel</strong> — un env Bêta ne peut pas être promu en Pré-prod directement.
          La Pré-prod est toujours reconstruite from scratch via un déploiement dédié.
        </span>
      </aside>
    </>
  );
}

export function PromoteFromDevModal({
  slot, devEnvs, onClose, onLaunch,
}: {
  slot: BetaSlot; devEnvs: DevEnv[]; onClose: () => void; onLaunch: (devEnvId: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);

  // Le clone (stop + wipe + cp NFS + reinstall) dure plusieurs minutes : on délègue au parent
  // qui le lance en arrière-plan, et on ferme la popup tout de suite.
  const launch = () => {
    if (!selected) return;
    onLaunch(selected);
    onClose();
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Slot Bêta ${slot.slot}`}
      subtitle={slot.currentDevEnvId
        ? `Remplacer le contenu actuel (${slot.currentDevEnvDisplay ?? 'env inconnu'}) — lancé en arrière-plan`
        : 'Choisis un dev env à cloner dans ce slot'}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" onClick={launch}
          disabled={!selected}
          leftIcon={<Icon name="arrow-right" size={14} />}>
          Pousser
        </Button>
      </>}
    >
      {devEnvs.length === 0 ? (
        <div className={styles.empty}>Aucun dev env disponible (il faut au moins un env non-errored).</div>
      ) : (
        <div className={styles.devApisPicker}>
          {devEnvs.map((e) => {
            const on = selected === e.id;
            return (
              <label key={e.id} className={[styles.devApiTile, on && styles.devApiTileActive].filter(Boolean).join(' ')}>
                <input type="radio" checked={on} onChange={() => setSelected(e.id)} />
                <span className={styles.devApiCheck} aria-hidden>{on && <Icon name="check" size={10} />}</span>
                <div className={styles.devApiText}>
                  <div className={styles.devApiTitle}>
                    <span>{e.displayName}</span>
                    <Badge tone="info">{e.containerCode}</Badge>
                  </div>
                  <div className={styles.devApiSub}>
                    par {e.ownerName} · {e.status}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ============================================================
//  PREPROD / PROD — vue stable
// ============================================================

interface StableViewProps {
  tabs: ReactNode;
  envId: 'preprod' | 'prod';
  projectId: string;
}

function StableView({ tabs, envId, projectId }: StableViewProps) {
  const navigate = useNavigate();
  const user = useSessionStore((s) => s.user);

  // Containers réels (depuis l'API) — mis à jour à chaque release qui atteint "released"
  const allContainers = useContainersStore((s) => s.containers);
  const loadContainers = useContainersStore((s) => s.load);
  useEffect(() => {
    if (projectId) void loadContainers(projectId);
  }, [projectId, loadContainers]);

  const containers = useMemo(
    () => allContainers.filter((c) => c.projectId === projectId),
    [allContainers, projectId],
  );

  // Instances réelles via Pterodactyl (preprod/prod). Si Ptero pas configuré → vide + message
  const [realInstances, setRealInstances] = useState<EnvironmentInstance[]>([]);
  const [pteroConfigured, setPteroConfigured] = useState<boolean>(true);
  const [pteroMessage, setPteroMessage] = useState<string | null>(null);
  const [instancesLoading, setInstancesLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setInstancesLoading(true);
    environmentsService.listInstances(projectId, envId)
      .then((r) => {
        setRealInstances(r.instances);
        setPteroConfigured(r.configured);
        setPteroMessage(r.message ?? null);
      })
      .catch(() => {
        setRealInstances([]);
        setPteroConfigured(false);
        setPteroMessage(null);
      })
      .finally(() => setInstancesLoading(false));
  }, [projectId, envId]);

  // Panneau mock legacy (dormant, souvent vide) — la vraie data viendra avec phase 4.
  const apis = useMemo(
    () => MOCK_APIS.filter((a) => a.projectId === projectId && a.environment === envId),
    [projectId, envId],
  );

  const [restartOpen, setRestartOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [instanceFilter, setInstanceFilter] = useState<string>('all');
  // Groupe de jeu des serveurs (RECUBE_GROUP), NationsGlory seulement : badge, filtre et alerte
  // « groupe inconnu ». Ailleurs, aucune requête de plus et la page reste celle d'avant.
  const project = useSessionStore((s) => s.currentProject());
  const { enabled: groupsEnabled, groups: gameGroups } = useProjectGameGroups(project);
  const [groupFilter, setGroupFilter] = useState<string>(GROUP_FILTER_ALL);
  const groupChips = useMemo(
    () => (groupsEnabled && gameGroups ? groupFilterChips(realInstances, gameGroups) : []),
    [groupsEnabled, gameGroups, realInstances],
  );
  const unknownMessage = useMemo(
    () => (groupsEnabled && gameGroups ? unknownGroupsMessage(unknownGroups(realInstances, gameGroups), gameGroups) : null),
    [groupsEnabled, gameGroups, realInstances],
  );
  // Filtre sans puce (groupe retiré, inconnus corrigés) : retour à « Tous »
  useEffect(() => {
    const next = validGroupFilter(groupFilter, groupChips);
    if (next !== groupFilter) setGroupFilter(next);
  }, [groupChips, groupFilter]);
  const inGroup = (i: EnvironmentInstance) =>
    groupFilter === GROUP_FILTER_ALL || matchesGroupFilter(instanceGroupState(i.group, gameGroups), groupFilter);
  const canRestart = can(user, 'cicd.restart');
  const canPromote = can(user, 'cicd.deploy.prod');

  // Instances regroupées par container (proxy ensemble, faction ensemble…), rendues en cards.
  const instanceGroups = useMemo(() => {
    const map = new Map<string, { code: string | null; container: MinecraftContainer | undefined; instances: EnvironmentInstance[] }>();
    for (const i of realInstances) {
      const key = i.containerCode ?? '__none__';
      let g = map.get(key);
      if (!g) {
        const container = i.containerCode ? containers.find((c) => c.code === i.containerCode) : undefined;
        g = { code: i.containerCode ?? null, container, instances: [] };
        map.set(key, g);
      }
      g.instances.push(i);
    }
    // Ordre par défaut : alphabétique, mais "build" (infra) puis "Autres" (sans container) en dernier.
    const rank = (g: { code: string | null }) => (!g.code ? 2 : g.code.toLowerCase() === 'build' ? 1 : 0);
    return [...map.values()].sort((a, b) => {
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return (a.container?.displayName ?? a.code ?? '').localeCompare(b.container?.displayName ?? b.code ?? '');
    });
  }, [realInstances, containers]);

  // Ordre personnalisé des catégories (drag-and-drop), persisté par projet dans localStorage.
  const orderKey = `deploy.catOrder.${projectId}`;
  const [customOrder, setCustomOrder] = useState<string[]>(() => {
    try { const raw = localStorage.getItem(orderKey); return raw ? (JSON.parse(raw) as string[]) : []; }
    catch { return []; }
  });
  const [dragKey, setDragKey] = useState<string | null>(null);

  const groupKey = (g: { code: string | null }) => g.code ?? '__none__';

  // Resynchronise l'ordre quand on change de projet (StableView n'est pas remonté sur un switch de jeu).
  useEffect(() => {
    try { const raw = localStorage.getItem(orderKey); setCustomOrder(raw ? (JSON.parse(raw) as string[]) : []); }
    catch { setCustomOrder([]); }
  }, [orderKey]);

  const persistOrder = (next: string[]) => {
    setCustomOrder(next);
    try { localStorage.setItem(orderKey, JSON.stringify(next)); } catch { /* localStorage indispo : ignore */ }
  };

  // Applique l'ordre custom. « Autres » (sans container) reste TOUJOURS en dernier ; les catégories
  // non listées (nouveaux containers) gardent l'ordre par défaut (tri stable), juste avant « Autres ».
  const orderedGroups = useMemo(() => {
    const idx = new Map(customOrder.map((k, i) => [k, i]));
    return [...instanceGroups].sort((a, b) => {
      if (!a.code || !b.code) return (!a.code ? 1 : 0) - (!b.code ? 1 : 0); // « Autres » toujours en dernier
      const ra = idx.has(groupKey(a)) ? (idx.get(groupKey(a)) as number) : Infinity;
      const rb = idx.has(groupKey(b)) ? (idx.get(groupKey(b)) as number) : Infinity;
      return ra - rb;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceGroups, customOrder]);

  // Réinitialise le filtre si la catégorie sélectionnée n'existe plus (évite un panneau vide sans reset).
  useEffect(() => {
    if (instanceFilter !== 'all' && !orderedGroups.some((g) => groupKey(g) === instanceFilter)) {
      setInstanceFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedGroups, instanceFilter]);

  const reorder = (targetKey: string) => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); return; }
    const keys = orderedGroups.map(groupKey);
    const from = keys.indexOf(dragKey);
    const to = keys.indexOf(targetKey);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    keys.splice(from, 1);
    keys.splice(to, 0, dragKey);
    persistOrder(keys);
    setDragKey(null);
  };

  // Filtre par catégorie (container) : "Tous" ou un container précis.
  const byContainer = instanceFilter === 'all'
    ? orderedGroups
    : orderedGroups.filter((g) => groupKey(g) === instanceFilter);
  // Puis par groupe de jeu (NationsGlory) : catégories vidées masquées. « Tous » : rien ne change.
  const shownGroups = groupFilter === GROUP_FILTER_ALL
    ? byContainer
    : byContainer
      .map((g) => ({ ...g, instances: g.instances.filter(inGroup) }))
      .filter((g) => g.instances.length > 0);
  const groupFilteredCount = groupFilter === GROUP_FILTER_ALL
    ? realInstances.length
    : realInstances.filter(inGroup).length;
  const activeGroupChip = groupChips.find((c) => c.key === groupFilter);

  return (
    <>
      <div className={styles.envHeaderRow}>
        {tabs}
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            leftIcon={<Icon name="refresh" size={16} />}
            onClick={() => setRestartOpen(true)}
            disabled={!canRestart}
          >
            Redémarrer…
            {!canRestart && <PermissionLock inline />}
          </Button>
          {envId === 'preprod' ? (
            <Button
              variant="primary"
              leftIcon={<Icon name="rocket" size={16} />}
              onClick={() => setPromoteOpen(true)}
              disabled={!canPromote}
              title="Promouvoir Pré-prod → Prod (copie exacte)"
            >
              Promouvoir vers Prod
              {!canPromote && <PermissionLock inline />}
            </Button>
          ) : (
            <Button
              variant="primary"
              leftIcon={<Icon name="rocket" size={16} />}
              onClick={() => navigate('/deploiement/deploy')}
            >
              Nouveau déploiement
            </Button>
          )}
        </div>
      </div>

      {envId === 'preprod' && (
        <aside className={styles.preprodBanner}>
          <Icon name="lock" size={14} />
          <span>
            <strong>Règle d'or :</strong> toute mise en production passe obligatoirement par la Pré-prod.
            Aucun déploiement direct en Prod — Bêta ne peut pas être promue non plus.
          </span>
        </aside>
      )}

      <section className={styles.panel}>
          <header className={styles.panelHeader}>
            <h2><Icon name="server" size={18} /> Instances Pterodactyl</h2>
            <Badge tone={pteroConfigured ? 'muted' : 'warning'}>{realInstances.length}</Badge>
          </header>
          <div className={styles.instanceGroups}>
            {instancesLoading && (
              <div className={styles.empty}>Chargement Pterodactyl…</div>
            )}
            {!instancesLoading && !pteroConfigured && (
              <div className={styles.empty} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                <Icon name="warn" size={20} />
                <div>{pteroMessage ?? 'Pterodactyl non configuré pour cet env'}</div>
                <Button variant="secondary" size="sm" leftIcon={<Icon name="settings" size={12} />}
                  onClick={() => navigate('/deploiement/settings')}>
                  Aller dans Settings
                </Button>
              </div>
            )}
            {!instancesLoading && pteroConfigured && realInstances.length === 0 && (
              <div className={styles.empty}>Aucune instance Pterodactyl trouvée pour cet env.</div>
            )}
            {!instancesLoading && pteroConfigured && unknownMessage && (
              <div className={styles.groupAlert} role="alert">
                <Icon name="warn" size={14} />
                <span>{unknownMessage}</span>
                {groupFilter !== GROUP_FILTER_UNKNOWN && (
                  <button type="button" className={styles.groupAlertAction} onClick={() => setGroupFilter(GROUP_FILTER_UNKNOWN)}>
                    Afficher
                  </button>
                )}
              </div>
            )}
            {!instancesLoading && pteroConfigured
              && ((realInstances.length > 0 && groupChips.length > 0) || orderedGroups.length > 1) && (
              // Groupes de jeu et catégories (containers) sur une même ligne, chacun avec son libellé.
              <div className={styles.instanceFilterBar}>
                {realInstances.length > 0 && groupChips.length > 0 && (
                  <div className={styles.instanceFilters} role="group" aria-label="Filtrer par groupe de jeu">
                    <span className={styles.filterLabel}>Groupe</span>
                    {groupChips.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={[
                          styles.filterChip,
                          groupFilter === c.key && styles.filterChipActive,
                          c.kind === 'unknown' && styles.filterChipWarning,
                        ].filter(Boolean).join(' ')}
                        onClick={() => setGroupFilter(c.key)}
                        title={c.kind === 'group' ? `Serveurs dont RECUBE_GROUP vaut ${c.key}`
                          : c.kind === 'none' ? 'Serveurs sans RECUBE_GROUP : ils reçoivent les API Principales'
                          : c.kind === 'unknown' ? 'RECUBE_GROUP absent des groupes du projet' : undefined}
                      >
                        {c.kind === 'group' && (
                          <span className={styles.filterDot} style={c.color ? { background: c.color } : undefined} />
                        )}
                        {c.kind === 'unknown' && <Icon name="warn" size={11} />}
                        {c.label} <span className={styles.filterCount}>{c.count}</span>
                      </button>
                    ))}
                  </div>
                )}
                {orderedGroups.length > 1 && (
                  <div className={styles.instanceFilters} role="group" aria-label="Filtrer par catégorie">
                    <span className={styles.filterLabel}>Catégorie</span>
                    <button
                      type="button"
                      className={`${styles.filterChip} ${instanceFilter === 'all' ? styles.filterChipActive : ''}`}
                      onClick={() => setInstanceFilter('all')}
                    >
                      Tous <span className={styles.filterCount}>{groupFilteredCount}</span>
                    </button>
                    {orderedGroups.map((g) => {
                      const key = g.code ?? '__none__';
                      const label = g.code ? (g.container?.displayName ?? g.code) : 'Autres';
                      const count = groupFilter === GROUP_FILTER_ALL ? g.instances.length : g.instances.filter(inGroup).length;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`${styles.filterChip} ${instanceFilter === key ? styles.filterChipActive : ''}`}
                          onClick={() => setInstanceFilter(key)}
                        >
                          {label} <span className={styles.filterCount}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {!instancesLoading && pteroConfigured && realInstances.length > 0 && shownGroups.length === 0
              && activeGroupChip && activeGroupChip.kind !== 'all' && (
              <div className={styles.empty}>
                Aucun serveur {activeGroupChip.kind === 'group' ? `du groupe ${activeGroupChip.label}`
                  : activeGroupChip.kind === 'none' ? 'sans groupe' : 'à groupe inconnu'} ici
                {instanceFilter !== 'all' ? ' pour ce container' : ''}.
              </div>
            )}
            {!instancesLoading && pteroConfigured && shownGroups.map((g) => (
              <div
                key={groupKey(g)}
                className={`${styles.instanceGroup} ${dragKey === groupKey(g) ? styles.instanceGroupDragging : ''}`}
                onDragOver={(e) => { if (instanceFilter === 'all' && dragKey) e.preventDefault(); }}
                onDrop={() => { if (instanceFilter === 'all') reorder(groupKey(g)); }}
              >
                <div
                  className={styles.instanceGroupHeader}
                  draggable={instanceFilter === 'all' && !!g.code}
                  onDragStart={(e) => {
                    // dataTransfer.setData est requis par Firefox pour initier le drag HTML5.
                    e.dataTransfer.setData('text/plain', groupKey(g));
                    e.dataTransfer.effectAllowed = 'move';
                    setDragKey(groupKey(g));
                  }}
                  onDragEnd={() => setDragKey(null)}
                >
                  {instanceFilter === 'all' && g.code && (
                    <span className={styles.dragHandle} title="Glisser pour réordonner la catégorie">
                      <Icon name="menu" size={13} />
                    </span>
                  )}
                  <span className={styles.containerChip} style={{ background: g.container?.color ?? '#999' }}>
                    <Icon name="package" size={12} />
                  </span>
                  {g.code ? (
                    <button
                      type="button"
                      className={styles.groupTitleLink}
                      onClick={() => navigate(`/deploiement/containers/${g.code}`)}
                      title={`Voir le container ${g.container?.displayName ?? g.code}`}
                    >
                      {g.container?.displayName ?? g.code}
                    </button>
                  ) : (
                    <span className={styles.groupTitle}>Autres</span>
                  )}
                  <Badge tone="muted">{g.instances.length}</Badge>
                </div>
                {/* Une ligne par serveur, les unes sous les autres, comme sur la page des API. */}
                <div className={styles.instanceRows}>
                  {g.instances.map((i) => (
                    <div
                      key={i.pteroId}
                      className={styles.instanceRow}
                      onClick={(e) => {
                        // Toute la ligne ouvre l'instance, sauf ses boutons (contrôles, container), une sélection
                        // de texte, et ce qui vient d'une fenêtre ouverte depuis la ligne (console : hors de son DOM).
                        if (!e.currentTarget.contains(e.target as Node)) return;
                        if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [role="dialog"]')) return;
                        if (window.getSelection()?.toString()) return;
                        navigate(`/deploiement/environments/${envId}/instance/${i.identifier}`);
                      }}
                    >
                      <div className={styles.instanceMain}>
                        <span className={styles.instanceChip} style={{ background: g.container?.color ?? '#999' }}>
                          <Icon name="server" size={12} />
                        </span>
                        <div className={styles.instanceInfo}>
                          <div className={styles.instanceTitle}>
                            <button
                              type="button"
                              className={styles.instanceOpen}
                              title="Ouvrir le détail (console + fichiers)"
                              onClick={() => navigate(`/deploiement/environments/${envId}/instance/${i.identifier}`)}
                            >
                              <span className={styles.instanceName} title={i.name}>{i.name}</span>
                              <Badge tone="muted">{i.identifier}</Badge>
                              <Icon name="chevron-right" size={14} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                            </button>
                            {groupsEnabled && <InstanceGroupTag raw={i.group} groups={gameGroups} />}
                            {i.containerCode && (
                              <button
                                type="button"
                                className={styles.instanceContainerBadge}
                                onClick={() => navigate(`/deploiement/containers/${i.containerCode}`)}
                                title={`Container utilisé : ${g.container?.displayName ?? i.containerCode} — voir le détail`}
                              >
                                <span className={styles.instanceContainerDot} style={{ background: g.container?.color ?? '#999' }} />
                                <Icon name="package" size={11} />
                                <span className={styles.instanceContainerName}>{g.container?.displayName ?? i.containerCode}</span>
                              </button>
                            )}
                          </div>
                          <code className={styles.instanceImage} title={i.image}>{i.image}</code>
                        </div>
                      </div>
                      <div className={styles.instanceControls}>
                        <ServerControls
                          projectId={projectId}
                          scope={`mc-${envId}` as const}
                          targetKey={i.identifier}
                          sourceName={i.name}
                          sourceSubtitle={`${envId} · ${i.image}`}
                          sourceAccent={g.container?.color}
                          icon="server"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>



      {/* Panel mock (legacy) — sera viré une fois toutes les envs migrées */}
      {apis.length > 0 && (
        <section className={styles.panel}>
          <header className={styles.panelHeader}>
            <h2><Icon name="zap" size={18} /> API services (mock legacy)</h2>
            <Badge tone="muted">{apis.length}</Badge>
          </header>
          <div className={styles.repoList}>
            {apis.map((a) => (
              <div key={a.id} className={styles.repoRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className={[styles.health, styles[`health_${a.health}`]].join(' ')}><span className={styles.healthDot} /></span>
                  <div>
                    <div className={styles.repoName}>{a.name}</div>
                    <div className={styles.repoDesc}>
                      {a.runtime === 'screen' ? '🖥 screen' : '🐳 container'} · v{a.version}
                    </div>
                  </div>
                </div>
                <div className={styles.repoVersion}>
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="eye" size={14} />} onClick={() => navigate(`/deploiement/logs?api=${a.id}`)}>Logs</Button>
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={14} />} disabled={!canRestart}>Restart</Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <RestartModal open={restartOpen} onClose={() => setRestartOpen(false)} envId={envId} />
      {envId === 'preprod' && (
        <PromoteToProdModal open={promoteOpen} onClose={() => setPromoteOpen(false)} />
      )}
    </>
  );
}

/**
 * Groupe de jeu d'un serveur (NationsGlory) : badge du groupe, alerte s'il n'est pas un groupe du
 * projet, rien sans RECUBE_GROUP. Groupes non chargés : la clé seule, sans verdict.
 */
function InstanceGroupTag({ raw, groups }: { raw?: string; groups: ProjectGroup[] | null }) {
  const state = instanceGroupState(raw, groups);
  if (state.kind === 'none') return null;
  if (state.kind === 'unknown') {
    return (
      <span
        className={styles.instanceGroupUnknown}
        title={`RECUBE_GROUP = ${state.key} : ce groupe n'existe pas dans le CICD. Le serveur reçoit les API Principales `
          + 'et aucun satellite filtré par groupe ne l\'affiche en Supervision.'}
      >
        <Icon name="warn" size={11} />
        <span className={styles.instanceGroupUnknownKey}>Groupe inconnu : {state.key}</span>
      </span>
    );
  }
  const display = groupDisplay(raw, groups);
  if (!display) return null;
  return (
    <span className={styles.instanceGroupTag}>
      <GroupBadge
        group={display}
        size="md"
        title={state.kind === 'known' ? `RECUBE_GROUP = ${state.group.key}`
          : `RECUBE_GROUP = ${state.key} (groupes du projet indisponibles : non vérifié)`}
      />
    </span>
  );
}

// ---------- Modals ----------

function RestartModal({ open, onClose, envId }: { open: boolean; onClose: () => void; envId: EnvironmentId }) {
  const services = MOCK_INSTANCES.filter((s) => s.environment === envId);
  const [selected, setSelected] = useState<Set<string>>(new Set(services.map((s) => s.id)));
  const [scheduled, setScheduled] = useState(false);
  const [when, setWhen] = useState('');

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Redémarrer — ${ENVIRONMENT_BY_ID[envId].name}`}
      subtitle="Sélectionner les instances à redémarrer, et choisir quand."
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" leftIcon={<Icon name="refresh" size={14} />} onClick={onClose} disabled={selected.size === 0 || (scheduled && !when)}>
            {scheduled ? 'Planifier' : 'Redémarrer maintenant'}
          </Button>
        </>
      }
    >
      <div className={styles.restart}>
        {services.map((s) => (
          <label key={s.id} className={[styles.restartRow, selected.has(s.id) && styles.restartActive].filter(Boolean).join(' ')}>
            <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
            <span className={styles.restartCheck} aria-hidden>{selected.has(s.id) && <Icon name="check" size={12} />}</span>
            <div>
              <div className={styles.serviceName}>{s.name}</div>
              <div className={styles.serviceMeta}>{s.node} · {s.runningVersion}</div>
            </div>
            {s.players !== undefined && <Badge tone="info">{formatNumber(s.players)} joueurs</Badge>}
          </label>
        ))}
        <div className={styles.restartOptions}>
          <Toggle
            checked={scheduled}
            onChange={setScheduled}
            label="Planifier le redémarrage"
            description="Par défaut : immédiat."
          />
          {scheduled && (
            <input type="datetime-local" className={styles.schedule} value={when} onChange={(e) => setWhen(e.target.value)} />
          )}
        </div>
      </div>
    </Modal>
  );
}


function PromoteToProdModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const project = useSessionStore((s) => s.currentProject());
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [skipEggs, setSkipEggs] = useState(false);
  const [launching, setLaunching] = useState<'run' | 'dry' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Multi-procédures : on charge toutes les procédures prod et l'user en choisit une
  const [proceduresByName, setProceduresByName] = useState<Record<string, UpdateProcedure>>({});
  const [selectedProcName, setSelectedProcName] = useState<string>('Défaut');
  const procedurePreview = proceduresByName[selectedProcName] ?? null;
  // Ordre réellement joué (promotion d'un bloc), calculé par le CICD sans appeler Pterodactyl
  const [plan, setPlan] = useState<PromotionPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const { procedureService } = await import('@/deploy/services/procedure.service');
        const all = await procedureService.listByName(project.id, 'prod');
        setProceduresByName(all);
        // Sélection auto : "Défaut" si dispo, sinon 1ère
        const names = Object.keys(all);
        if (names.length > 0) {
          setSelectedProcName(names.includes('Défaut') ? 'Défaut' : names[0]);
        }
      } catch { setProceduresByName({}); }
    })();
  }, [open, project.id]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setPlan(null);
    setPlanError(null);
    void (async () => {
      try {
        const { promotionService } = await import('@/deploy/services/promotion.service');
        const p = await promotionService.plan(project.id, 'prod', selectedProcName);
        if (alive) setPlan(p);
      } catch (e) {
        if (alive) setPlanError(e instanceof Error ? e.message : 'Erreur inconnue');
      }
    })();
    return () => { alive = false; };
  }, [open, project.id, selectedProcName]);

  // Lancement NON bloquant : le job tourne côté backend ; on ferme la popup et on redirige
  // vers la page de suivi partagée (« Suivi déploiement »), au lieu de bloquer sur la popup.
  // La simulation à vide lit Pterodactyl sans rien y écrire.
  const launch = async (dryRun: boolean) => {
    setError(null);
    setLaunching(dryRun ? 'dry' : 'run');
    try {
      const { promotionService } = await import('@/deploy/services/promotion.service');
      await promotionService.start(project.id, {
        targetEnv: 'prod',
        sourceEnv: 'preprod',
        dryRun,
        procedureName: selectedProcName,
        skipEggs: skipEggs || undefined,
      });
      onClose();
      navigate('/deploiement/deployments');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setLaunching(null);
    }
  };

  const refusedBeforeStart = (plan?.errors.length ?? 0) > 0;
  const stepCount = plan ? plan.items.length : (procedurePreview?.steps.length ?? 0);
  const subtitle = plan?.bloc
    ? `Promotion d'un bloc (${stepCount} étapes) : proxy arrêté d'abord, redémarré en dernier. Le déroulé se suit sur la page « Suivi déploiement », tu peux fermer sans interrompre.`
    : `Lance la procédure configurée pour prod (${stepCount} étapes). Le déroulé se suit sur la page « Suivi déploiement », tu peux fermer sans interrompre.`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Promouvoir Pré-prod → Prod"
      subtitle={subtitle}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={launching !== null}>Annuler</Button>
          <Button variant="secondary" leftIcon={<Icon name="eye" size={14} />}
            onClick={() => void launch(true)} disabled={launching !== null} loading={launching === 'dry'}
            title="Lit Pterodactyl et montre l'ordre et les contrôles, sans rien arrêter ni modifier">
            Simulation à vide
          </Button>
          <Button variant="primary" leftIcon={<Icon name="rocket" size={14} />}
            onClick={() => void launch(false)} disabled={!confirmed || launching !== null || refusedBeforeStart}
            loading={launching === 'run'}>
            Promouvoir maintenant
          </Button>
        </>
      }
    >
      <div className={styles.promoteBox}>
            {/* Sélecteur de procédure */}
            {Object.keys(proceduresByName).length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px',
                background: 'var(--color-background-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                marginBottom: 12,
                flexWrap: 'wrap',
              }}>
                <Icon name="rocket" size={14} />
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Procédure à exécuter
                </label>
                <select
                  value={selectedProcName}
                  onChange={(e) => setSelectedProcName(e.target.value)}
                  disabled={launching !== null}
                  style={{
                    padding: '4px 8px',
                    fontSize: 13,
                    borderRadius: 4,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text)',
                    minWidth: 220,
                  }}
                >
                  {Object.entries(proceduresByName).map(([name, proc]) => (
                    <option key={name} value={name}>{name} ({proc.steps.length} étapes)</option>
                  ))}
                </select>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  Gérable dans Settings → Procédure d'update
                </span>
              </div>
            )}

            {plan ? (
              <PromotionPlanPreview plan={plan} procName={selectedProcName} />
            ) : planError ? (
              <>
                <div className={styles.planNote}>
                  <Icon name="warn" size={12} />
                  <span>Ordre joué indisponible ({planError}) : voici la procédure telle qu'enregistrée.</span>
                </div>
                <StoredProcedurePreview procName={selectedProcName} procedure={procedurePreview} />
              </>
            ) : (
              <div className={styles.planNote}><Icon name="clock" size={12} /><span>Calcul de l'ordre joué…</span></div>
            )}

            {plan?.bloc && (
              <Toggle
                checked={skipEggs}
                onChange={setSkipEggs}
                label="Sans le modèle des lobbies de Nexus (eggs)"
                description="Seulement si la tournée ne change pas la liste d'objets : Nexus continuerait à créer ses lobbies sur l'ancienne image. Sans cette case, une promotion dont l'egg ne peut pas être mis à jour est refusée avant tout arrêt."
              />
            )}
            <Toggle
              checked={confirmed}
              onChange={setConfirmed}
              label="Je confirme — la prod va être impactée"
              description="Vérifie les credentials Pterodactyl + CDN prod avant de lancer. La simulation à vide, elle, ne touche à rien."
            />

        {error && <div style={{ padding: 10, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 6, fontSize: 13 }}>{error}</div>}

        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <Icon name="rocket" size={12} /> Au lancement, tu seras redirigé vers « Suivi déploiement » (visible par tous). Le job continue côté serveur même si tu fermes.
        </div>
      </div>
    </Modal>
  );
}

/** Ordre joué par la promotion, regroupé par phase (proxy arrêté d'abord, redémarré en dernier). */
function PromotionPlanPreview({ plan, procName }: { plan: PromotionPlan; procName: string }) {
  const groups: { phase: string; label: string; items: PromotionPlan['items'] }[] = [];
  for (const it of plan.items) {
    const last = groups[groups.length - 1];
    if (last && last.phase === it.phase) last.items.push(it);
    else groups.push({ phase: it.phase, label: it.phaseLabel, items: [it] });
  }
  let n = 0;
  return (
    <div className={styles.planBox}>
      {plan.bloc ? (
        <div className={styles.planNote}>
          <Icon name="lock" size={12} />
          <span>
            <strong>Promotion d'un bloc :</strong> le CICD arrête le proxy avant de toucher aux serveurs de jeu et le
            redémarre en dernier, quel que soit l'ordre de la procédure « {procName} ». Les étapes proxy de la procédure
            sont jouées à ces deux moments, et les proxys sont retirés des stop et start des autres étapes.
          </span>
        </div>
      ) : (
        <div className={styles.planNote}>
          <Icon name="clock" size={12} />
          <span>Cette procédure ne met aucune image à jour : elle est jouée telle qu'enregistrée, sans arrêt du proxy imposé.</span>
        </div>
      )}
      {plan.errors.length > 0 && (
        <div className={styles.planErrors} role="alert">
          <strong>La promotion sera refusée avant tout arrêt :</strong>
          <ul>{plan.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      <ol className={styles.planList}>
        {groups.map((g, gi) => (
          <li key={gi} className={styles.planGroup}>
            <span className={styles.planPhase}>{g.label}</span>
            <ol>
              {g.items.map((it) => {
                n += 1;
                return (
                  <li key={`${it.phase}-${it.storedIndex}-${n}`} value={n}>
                    <strong style={{ color: it.builtin ? 'var(--color-primary)' : 'var(--color-text)' }}>{it.label}</strong>
                    {it.storedIndex > 0 && <span className={styles.planIndex}> n°{it.storedIndex}</span>}
                    {it.detail && <span> : {it.detail}</span>}
                  </li>
                );
              })}
            </ol>
          </li>
        ))}
      </ol>
      {plan.joinedStop.length > 0 && <div className={styles.planMeta}>Jouées par l'arrêt du proxy : {plan.joinedStop.join(', ')}</div>}
      {plan.joinedStart.length > 0 && <div className={styles.planMeta}>Jouées par le redémarrage du proxy : {plan.joinedStart.join(', ')}</div>}
      {plan.skipped.length > 0 && (
        <div className={styles.planMeta}>
          Modifiées par le CICD :
          <ul>{plan.skipped.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {plan.warnings.length > 0 && (
        <div className={styles.planWarnings}>
          <strong>À vérifier dans la procédure :</strong>
          <ul>{plan.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** Procédure telle qu'enregistrée (CICD sans l'aperçu de l'ordre joué). */
function StoredProcedurePreview({ procName, procedure }: { procName: string; procedure: UpdateProcedure | null }) {
  if (!procedure || procedure.steps.length === 0) {
    return (
      <div style={{ padding: 10, background: 'var(--color-warning-bg)', color: 'var(--color-warning)', borderRadius: 6, fontSize: 13 }}>
        <Icon name="warn" size={12} /> Aucune procédure configurée pour prod — va dans Settings → Procédure d'update pour la définir.
      </div>
    );
  }
  return (
    <>
      <p>Cette action va exécuter la procédure <strong>"{procName}"</strong> :</p>
      <ol style={{ fontSize: 13, paddingLeft: 20, margin: '0 0 12px' }}>
        {procedure.steps.map((s, i) => (
          <li key={i} style={{ marginBottom: 2, color: 'var(--color-text-muted)' }}>
            <strong style={{ color: 'var(--color-text)' }}>{s.label || s.type}</strong>
            {s.type === 'sleep' && <span> — {s.sleepSeconds}s</span>}
            {s.type === 'command' && <span> — <code>{s.command}</code> ({s.imageFilter})</span>}
            {s.type === 'power' && <span> — {s.powerSignal} ({s.imageFilter})</span>}
            {s.type === 'update-images' && <span> — filtre <code>{s.imageFilter}</code></span>}
            {s.type === 'cdn-promote' && <span> — RPs vers CDN prod</span>}
          </li>
        ))}
      </ol>
    </>
  );
}
